import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { ContentPieceDetail, PieceKind } from '../../shared/api/types';
import { Alert, Button, Field } from '../../shared/ui/components';
import { KIND } from './labels';

/**
 * Crear o editar una pieza. Editar guarda una versión nueva (las anteriores no cambian) y la
 * regresa a borrador; `baseRevision` evita pisar lo que otra persona guardó mientras tanto.
 */
export function PieceForm({ churchId, piece, onSaved, onCancel }: { churchId: string; piece?: ContentPieceDetail; onSaved: (p: ContentPieceDetail) => void; onCancel?: () => void }) {
  const [kind, setKind] = useState<PieceKind>(piece?.kind ?? 'sermon');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const r = piece?.revision;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? '').trim();
    const body = {
      title: text('title'),
      summary: text('summary') || null,
      body: text('body'),
      preacher: kind === 'sermon' ? text('preacher') || null : null,
      passage: kind === 'sermon' ? text('passage') || null : null,
      preachedOn: kind === 'sermon' ? text('preachedOn') || null : null,
    };
    if (body.title.length < 3) return setError('Escribe un título de al menos 3 letras.');
    if (!body.body) return setError('Escribe el contenido.');
    setBusy(true);
    setError(null);
    try {
      onSaved(
        piece
          ? await api.put<ContentPieceDetail>(`/content/churches/${churchId}/pieces/${piece.id}`, { ...body, baseRevision: piece.currentRevision })
          : await api.post<ContentPieceDetail>(`/content/churches/${churchId}/pieces`, { ...body, kind }),
      );
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      {piece ? null : (
        <fieldset className="choices choices--row">
          <legend>Tipo</legend>
          {(Object.keys(KIND) as PieceKind[]).map((k) => (
            <label key={k} className="choice">
              <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
              <span>{KIND[k]}</span>
            </label>
          ))}
        </fieldset>
      )}
      {piece && piece.status !== 'draft' ? <Alert tone="info">Al guardar se crea la versión {piece.currentRevision + 1} y vuelve a borrador. Lo ya publicado no cambia.</Alert> : null}
      <Field label="Título" name="title" required minLength={3} maxLength={160} defaultValue={r?.title} />
      <Field label="Resumen (opcional)" name="summary" maxLength={400} defaultValue={r?.summary ?? ''} />
      {kind === 'sermon' ? (
        <div className="grid-2">
          <Field label="Predicador" name="preacher" maxLength={120} defaultValue={r?.preacher ?? ''} />
          <Field label="Texto bíblico" name="passage" maxLength={120} placeholder="Juan 3:16" defaultValue={r?.passage ?? ''} />
          <Field label="Fecha" name="preachedOn" type="date" defaultValue={r?.preachedOn ?? ''} />
        </div>
      ) : null}
      <div className="field">
        <label htmlFor="piece-body">Contenido</label>
        <textarea id="piece-body" name="body" required rows={12} maxLength={50_000} defaultValue={r?.body} aria-describedby="piece-body-hint" />
        <p id="piece-body-hint" className="field__hint">
          Separa los párrafos con una línea en blanco.
        </p>
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          {piece ? 'Guardar versión' : 'Guardar borrador'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}
